import { useEffect, useMemo } from 'react';

import { useSale } from '../../hooks/sale/useSale';

import { usePaymentCondition } from '../../hooks/usePaymentCondition';

import { useAllowedOptions } from '../../hooks/settings/useAllowedOptions';

import { useTerm } from '../../hooks/term/useTerm';

import { useMultiplePayment } from '../../hooks/sale/useMultiplePayment';

const CREDIT_CODE = 'CREDITO';

const OTHER_CODE = 'OTRO';

const CASH_CODE = 'CONTADO';

export const usePOSPaymentCondition = () => {
    const {
        form,
        updateField,
        setPaymentConditionCodeFn,
        openMultiPaymentModal,
        clearAllPayments,
    } = useSale();

    const {
        paymentConditionDropdown,
        fetchPaymentConditionDropdown,
    } = usePaymentCondition();

    const {
        termDropdown,
        fetchTermDropdown,
    } = useTerm();

    const {
        getCodes,
        fetchCodesByModuleAndTarget,
    } = useAllowedOptions();

    const { fetchPayments } = useMultiplePayment();

    // =====================================================
    // 🔹 Init
    // =====================================================
    useEffect(() => {
        fetchPaymentConditionDropdown();

        fetchTermDropdown();

        fetchCodesByModuleAndTarget(
            'SALES',
            'PAYMENT_CONDITION'
        );
    }, []);

    // =====================================================
    // 🔹 Allowed Conditions
    // =====================================================
    const allowedPaymentCodes = getCodes(
        'SALES',
        'PAYMENT_CONDITION'
    );

    const filteredPaymentConditions = useMemo(() => {
        return paymentConditionDropdown.filter((item) =>
            allowedPaymentCodes.includes(item.code)
        );
    }, [
        paymentConditionDropdown,
        allowedPaymentCodes,
    ]);

    // =====================================================
    // 🔹 Selected Condition
    // =====================================================
    const selectedCondition =
        paymentConditionDropdown.find(
            (p) =>
                String(p.id) ===
                String(form.paymentCondition?.id)
        );

    // =====================================================
    // 🔹 Flags
    // =====================================================
    const isCredit =
        selectedCondition?.code === CREDIT_CODE;

    const isOtherCondition =
        selectedCondition?.code === OTHER_CODE;

    // =====================================================
    // 🔹 Handlers
    // =====================================================
    const handlePaymentConditionChange = (
        value: string | number
    ) => {
        const selected =
            paymentConditionDropdown.find(
                (opt) => String(opt.id) === String(value)
            );

        if (!selected) return;

        clearAllPayments();

        updateField('paymentCondition', {
            id: selected.id,
            name: selected.name,
            code: selected.code,

            termId: undefined,
            termName: undefined,
            termCode: undefined,

            creditDays: undefined,
            creditMonths: undefined,
            creditYears: undefined,

            creditDueDate: undefined,
        });

        setPaymentConditionCodeFn(selected.code);
    };

    // =====================================================
    // 🔹 Default: Contado
    // =====================================================
    // Agiliza el cobro: la mayoria de ventas son de contado, asi el
    // cajero solo confirma la forma de pago en vez de tener que elegir
    // la condicion cada vez. Solo aplica si la venta todavia no tiene
    // ninguna condicion guardada (no pisa "A credito"/"Otro" ya elegidos).
    useEffect(() => {
        if (form.paymentCondition?.id) return;
        if (!filteredPaymentConditions.length) return;

        const cashCondition = filteredPaymentConditions.find(
            (opt) => opt.code === CASH_CODE
        );

        if (!cashCondition) return;

        handlePaymentConditionChange(cashCondition.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.id, form.paymentCondition?.id, filteredPaymentConditions]);

    // =====================================================
    // 🔹 Mantener el resumen de pagos múltiples sincronizado
    // =====================================================
    // El resumen (Ingresado/Pendiente) se trae del backend con
    // fetchPayments y queda cacheado en Redux. Cualquier cambio que
    // recalcule el total de la venta (descuento global, agregar/quitar
    // productos...) mientras la condición es "Otro" debe refrescarlo,
    // o el cajero ve un "Pendiente" viejo y no logra cuadrar el pago.
    const totalValue = (form.totalsView?.items || []).find(
        (item: any) => item.isTotal
    )?.value;

    useEffect(() => {
        if (!isOtherCondition) return;
        if (!form.id) return;

        fetchPayments(form.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.id, isOtherCondition, totalValue]);

    const handleTermChange = (
        value: string | number
    ) => {
        const selectedTerm = termDropdown.find(
            (opt) => String(opt.id) === String(value)
        );

        if (!selectedTerm) return;

        updateField('paymentCondition', {
            ...form.paymentCondition,

            termId: selectedTerm.id,
            termName: selectedTerm.name,
            termCode: selectedTerm.code,

            creditDays: undefined,
            creditMonths: undefined,
            creditYears: undefined,

            creditDueDate: undefined,
        });
    };

    return {
        form,

        termDropdown,

        isCredit,

        isOtherCondition,

        filteredPaymentConditions,

        openMultiPaymentModal,

        handlePaymentConditionChange,

        handleTermChange,
    };
};